import { ComponentType } from "@/types";

// Chart property schema interfaces
export interface ChartPropertySchema {
  type: "string" | "number" | "boolean" | "color" | "array" | "object" | "enum";
  default?: any;
  description: string;
  aiAliases?: string[]; // Alternative names AI might use
  validation?: (value: any) => boolean;
  transform?: (value: any) => any; // Transform AI input to proper format
  enumValues?: string[];
}

export interface ChartTypeConfig {
  type: ComponentType;
  name: string;
  description: string;
  category: "comparison" | "trend" | "proportion" | "distribution" | "hierarchical" | "metric";
  properties: Record<string, ChartPropertySchema>;
  dataStructure: {
    required: string[];
    optional?: string[];
    format: "simple" | "series" | "nested" | "matrix";
  };
  aiCommands?: string[]; // Example commands for this chart type
  relatedTypes?: ComponentType[]; // Charts that can be converted to/from
}

// Common chart properties shared across multiple types
const commonChartProperties: Record<string, ChartPropertySchema> = {
  title: {
    type: "string",
    default: "",
    description: "Chart title",
    aiAliases: ["heading", "name", "label"],
  },
  description: {
    type: "string",
    default: "",
    description: "Chart description or subtitle",
    aiAliases: ["subtitle", "caption", "info"],
  },
  showLegend: {
    type: "boolean",
    default: true,
    description: "Display legend",
    aiAliases: ["legend", "key", "labels"],
  },
  showTooltip: {
    type: "boolean",
    default: true,
    description: "Show tooltips on hover",
    aiAliases: ["tooltips", "hover info", "popup"],
  },
  showGrid: {
    type: "boolean",
    default: true,
    description: "Display grid lines",
    aiAliases: ["gridlines", "grid", "background lines"],
  },
  animation: {
    type: "boolean",
    default: true,
    description: "Enable animations",
    aiAliases: ["animate", "transitions", "effects"],
  },
  colors: {
    type: "array",
    default: ["#8884d8", "#82ca9d", "#ffc658", "#ff7300"],
    description: "Color palette for the chart",
    aiAliases: ["color scheme", "palette", "theme colors"],
  },
  responsive: {
    type: "boolean",
    default: true,
    description: "Responsive sizing",
    aiAliases: ["auto resize", "flexible", "adaptive"],
  },
};

// Axis properties for cartesian charts
const axisProperties: Record<string, ChartPropertySchema> = {
  xAxisKey: {
    type: "string",
    default: "name",
    description: "Data key for X-axis",
    aiAliases: ["x axis", "horizontal axis", "category"],
  },
  yAxisKey: {
    type: "string",
    default: "value",
    description: "Data key for Y-axis",
    aiAliases: ["y axis", "vertical axis", "measure"],
  },
  xAxisLabel: {
    type: "string",
    default: "",
    description: "X-axis label",
    aiAliases: ["x label", "horizontal label"],
  },
  yAxisLabel: {
    type: "string",
    default: "",
    description: "Y-axis label",
    aiAliases: ["y label", "vertical label"],
  },
  xAxisType: {
    type: "enum",
    enumValues: ["number", "category"],
    default: "category",
    description: "X-axis data type",
    aiAliases: ["x type", "horizontal type"],
  },
  yAxisType: {
    type: "enum",
    enumValues: ["number", "category"],
    default: "number",
    description: "Y-axis data type",
    aiAliases: ["y type", "vertical type"],
  },
};

// Line chart specific properties
const lineChartProperties: Record<string, ChartPropertySchema> = {
  strokeWidth: {
    type: "number",
    default: 2,
    description: "Line thickness",
    aiAliases: ["line width", "thickness", "stroke", "line size"],
    validation: (value) => value >= 0.5 && value <= 10,
  },
  showDots: {
    type: "boolean",
    default: true,
    description: "Show data point markers",
    aiAliases: ["dots", "points", "markers", "data points"],
  },
  dotSize: {
    type: "number",
    default: 4,
    description: "Size of data point markers",
    aiAliases: ["dot radius", "point size", "marker size"],
    validation: (value) => value >= 1 && value <= 20,
  },
  lineType: {
    type: "enum",
    enumValues: [
      "linear", "monotone", "monotoneX", "monotoneY", "natural",
      "step", "stepBefore", "stepAfter",
      "basis", "basisOpen", "basisClosed", "bundle",
      "cardinal", "cardinalOpen", "cardinalClosed",
      "catmullRom"
    ],
    default: "monotone",
    description: "Line interpolation type",
    aiAliases: ["curve", "smoothing", "interpolation"],
  },
  connectNulls: {
    type: "boolean",
    default: true,
    description: "Connect lines across null values",
    aiAliases: ["skip nulls", "continuous", "bridge gaps"],
  },
  lineKeys: {
    type: "array",
    default: ["value"],
    description: "Data keys for multiple lines",
    aiAliases: ["series", "lines", "datasets"],
  },
  strokeDasharray: {
    type: "string",
    default: "",
    description: "Dash pattern for lines (e.g., '5 5' for dashed)",
    aiAliases: ["dashed", "dotted", "line style"],
    transform: (value: string) => {
      const patterns: Record<string, string> = {
        "solid": "",
        "continuous": "",
        "dashed": "5 5",
        "dotted": "2 2",
        "dot": "2 2",
        "long-dash": "10 5",
        "short-dash": "3 3",
        "dash-dot": "5 2 2 2",
        "dash-dot-dot": "5 2 2 2 2 2",
        "sparse": "15 10",
        "dense": "2 1",
        "morse": "10 2 2 2 10 2",
      };
      return patterns[value.toLowerCase()] || value;
    },
  },
  area: {
    type: "boolean",
    default: false,
    description: "Fill area under the line",
    aiAliases: ["fill", "area chart", "filled"],
  },
  areaOpacity: {
    type: "number",
    default: 0.3,
    description: "Opacity of filled area",
    aiAliases: ["fill opacity", "transparency"],
    validation: (value) => value >= 0 && value <= 1,
  },
  strokeOpacity: {
    type: "number",
    default: 1,
    description: "Line opacity",
    aiAliases: ["line opacity", "line transparency"],
    validation: (value) => value >= 0 && value <= 1,
  },
  dotShape: {
    type: "enum",
    enumValues: ["circle", "square", "diamond", "triangle", "star", "cross", "wye"],
    default: "circle",
    description: "Shape of data point markers",
    aiAliases: ["marker shape", "point shape"],
  },
  dotColor: {
    type: "string",
    default: "",
    description: "Color of data point marker stroke",
    aiAliases: ["marker color", "point color"],
  },
  dotFill: {
    type: "string",
    default: "",
    description: "Fill color of data point markers",
    aiAliases: ["marker fill", "point fill"],
  },
  dotStrokeWidth: {
    type: "number",
    default: 1,
    description: "Stroke width of data point markers",
    aiAliases: ["marker stroke", "point stroke width"],
    validation: (value) => value >= 0 && value <= 5,
  },
  tension: {
    type: "number",
    default: 0.5,
    description: "Curve tension for cardinal and catmullRom curves",
    aiAliases: ["smoothness", "curve tension"],
    validation: (value) => value >= 0 && value <= 1,
  },
};

// Bar chart specific properties
const barChartProperties: Record<string, ChartPropertySchema> = {
  barGap: {
    type: "number",
    default: 4,
    description: "Gap between bars",
    aiAliases: ["spacing", "bar spacing", "gap"],
  },
  barCategoryGap: {
    type: "number",
    default: 20,
    description: "Gap between bar categories",
    aiAliases: ["category gap", "group spacing"],
  },
  stacked: {
    type: "boolean",
    default: false,
    description: "Stack bars on top of each other",
    aiAliases: ["stacked bars", "cumulative"],
  },
  orientation: {
    type: "enum",
    enumValues: ["vertical", "horizontal"],
    default: "vertical",
    description: "Bar orientation",
    aiAliases: ["direction", "layout"],
  },
};

// Pie chart specific properties
const pieChartProperties: Record<string, ChartPropertySchema> = {
  innerRadius: {
    type: "number",
    default: 0,
    description: "Inner radius (0 for pie, >0 for donut)",
    aiAliases: ["donut", "hollow", "ring"],
    validation: (value) => value >= 0 && value <= 100,
  },
  outerRadius: {
    type: "number",
    default: 80,
    description: "Outer radius",
    aiAliases: ["size", "radius"],
    validation: (value) => value >= 10 && value <= 200,
  },
  showLabels: {
    type: "boolean",
    default: false,
    description: "Show value labels on slices",
    aiAliases: ["labels", "values", "percentages"],
  },
  startAngle: {
    type: "number",
    default: 0,
    description: "Starting angle in degrees",
    aiAliases: ["rotation", "start"],
  },
  endAngle: {
    type: "number",
    default: 360,
    description: "Ending angle in degrees",
    aiAliases: ["end", "arc"],
  },
  nameKey: {
    type: "string",
    default: "name",
    description: "Data key for slice names",
    aiAliases: ["label key", "category key"],
  },
  valueKey: {
    type: "string",
    default: "value",
    description: "Data key for slice values",
    aiAliases: ["data key", "amount key"],
  },
};


// Data Card properties schema
const dataCardProperties: Record<string, ChartPropertySchema> = {
  // Data properties
  value: {
    type: "string",
    default: "0",
    description: "The primary metric value to display",
    aiAliases: ["metric", "number", "amount", "stat"],
  },
  label: {
    type: "string",
    default: "",
    description: "Label text displayed above the value",
    aiAliases: ["title", "heading", "name"],
  },
  subtitle: {
    type: "string",
    default: "",
    description: "Subtitle text displayed below the value",
    aiAliases: ["description", "subtext"],
  },
  unit: {
    type: "string",
    default: "",
    description: "Unit suffix displayed after the value",
    aiAliases: ["suffix", "units"],
  },
  valueFormatter: {
    type: "enum",
    default: "number",
    description: "How to format the value",
    enumValues: ["number", "currency", "percent", "compact", "none"],
    aiAliases: ["format", "formatting"],
  },

  // Trend properties
  trendValue: {
    type: "number",
    default: 0,
    description: "Trend percentage value",
    aiAliases: ["change", "delta", "difference"],
  },
  trendLabel: {
    type: "string",
    default: "",
    description: "Label for the trend indicator",
    aiAliases: ["trend text", "change label"],
  },
  showTrendIcon: {
    type: "boolean",
    default: true,
    description: "Show up/down arrow for trend",
    aiAliases: ["trend arrow", "trend indicator"],
  },
  trendColorPositive: {
    type: "color",
    default: "#10B981",
    description: "Color for positive trend",
    aiAliases: ["positive color", "up color", "increase color"],
  },
  trendColorNegative: {
    type: "color",
    default: "#EF4444",
    description: "Color for negative trend",
    aiAliases: ["negative color", "down color", "decrease color"],
  },
  trendFontSize: {
    type: "string",
    default: "0.875rem",
    description: "Font size for trend text",
    aiAliases: ["trend size"],
  },

  // Icon properties
  icon: {
    type: "string",
    default: "",
    description: "Icon name from icon library",
    aiAliases: ["icon name", "symbol"],
  },
  iconPosition: {
    type: "enum",
    default: "left",
    description: "Position of the icon relative to content",
    enumValues: ["left", "right", "top", "none"],
    aiAliases: ["icon placement", "icon location"],
  },
  iconSize: {
    type: "number",
    default: 24,
    description: "Size of the icon in pixels",
    aiAliases: ["icon scale"],
    validation: (value) => value >= 12 && value <= 64,
  },
  iconColor: {
    type: "color",
    default: "#6B7280",
    description: "Color of the icon",
    aiAliases: ["icon fill"],
  },
  showIconBackground: {
    type: "boolean",
    default: false,
    description: "Show background circle behind icon",
    aiAliases: ["icon circle", "icon bg"],
  },
  iconBackgroundColor: {
    type: "color",
    default: "#F3F4F6",
    description: "Background color for the icon",
    aiAliases: ["icon bg color"],
  },
  iconBackgroundSize: {
    type: "number",
    default: 40,
    description: "Size of the icon background circle",
    aiAliases: ["icon bg size"],
    validation: (value) => value >= 24 && value <= 80,
  },

  // Layout properties
  layout: {
    type: "enum",
    default: "vertical",
    description: "Layout direction of the card content",
    enumValues: ["horizontal", "vertical"],
    aiAliases: ["direction", "orientation"],
  },
  gap: {
    type: "number",
    default: 8,
    description: "Gap between elements in pixels",
    aiAliases: ["spacing"],
    validation: (value) => value >= 0 && value <= 48,
  },
  valueAlignment: {
    type: "enum",
    default: "left",
    description: "Horizontal alignment of the value",
    enumValues: ["left", "center", "right"],
    aiAliases: ["align", "text align"],
  },
  alignItems: {
    type: "string",
    default: "flex-start",
    description: "Flex align-items property",
    aiAliases: ["vertical align"],
  },
  justifyContent: {
    type: "string",
    default: "flex-start",
    description: "Flex justify-content property",
    aiAliases: ["horizontal align"],
  },

  // Card styling
  cardBackgroundColor: {
    type: "color",
    default: "#FFFFFF",
    description: "Background color of the card",
    aiAliases: ["background", "bg color", "card color"],
  },
  cardBorderColor: {
    type: "color",
    default: "#E5E7EB",
    description: "Border color of the card",
    aiAliases: ["border color", "outline color"],
  },
  cardBorderWidth: {
    type: "number",
    default: 1,
    description: "Border width in pixels",
    aiAliases: ["border size", "border thickness"],
    validation: (value) => value >= 0 && value <= 8,
  },
  cardBorderRadius: {
    type: "number",
    default: 8,
    description: "Border radius in pixels",
    aiAliases: ["corner radius", "rounded corners"],
    validation: (value) => value >= 0 && value <= 32,
  },
  cardPadding: {
    type: "string",
    default: "16",
    description: "Padding inside the card (px or CSS value)",
    aiAliases: ["inner spacing", "content padding"],
  },
  cardShadow: {
    type: "string",
    default: "",
    description: "Box shadow CSS value",
    aiAliases: ["shadow", "elevation"],
  },

  // Label typography
  labelColor: {
    type: "color",
    default: "#6B7280",
    description: "Color of the label text",
    aiAliases: ["label text color", "title color"],
  },
  labelFontSize: {
    type: "string",
    default: "0.875rem",
    description: "Font size of the label",
    aiAliases: ["label size", "title size"],
  },
  labelFontWeight: {
    type: "number",
    default: 500,
    description: "Font weight of the label",
    aiAliases: ["label weight", "title weight"],
    validation: (value) => value >= 100 && value <= 900,
  },
  labelLetterSpacing: {
    type: "string",
    default: "0",
    description: "Letter spacing for the label",
    aiAliases: ["label spacing"],
  },

  // Value typography
  valueColor: {
    type: "color",
    default: "#1F2937",
    description: "Color of the value text",
    aiAliases: ["value text color", "number color"],
  },
  valueFontSize: {
    type: "string",
    default: "2rem",
    description: "Font size of the value",
    aiAliases: ["value size", "number size"],
  },
  valueFontWeight: {
    type: "number",
    default: 700,
    description: "Font weight of the value",
    aiAliases: ["value weight", "number weight"],
    validation: (value) => value >= 100 && value <= 900,
  },

  // Subtitle typography
  subtitleColor: {
    type: "color",
    default: "#9CA3AF",
    description: "Color of the subtitle text",
    aiAliases: ["subtitle text color"],
  },
  subtitleFontSize: {
    type: "string",
    default: "0.75rem",
    description: "Font size of the subtitle",
    aiAliases: ["subtitle size"],
  },
  subtitleFontWeight: {
    type: "number",
    default: 400,
    description: "Font weight of the subtitle",
    aiAliases: ["subtitle weight"],
    validation: (value) => value >= 100 && value <= 900,
  },
};

// Gauge chart properties
const gaugeChartProperties: Record<string, ChartPropertySchema> = {
  displayMode: {
    type: "enum",
    default: "dial",
    description: "Gauge visualization mode",
    enumValues: ["dial", "ring", "bar", "milestone"],
    aiAliases: ["mode", "gauge type", "style", "variant"],
  },
  value: {
    type: "number",
    default: 0,
    description: "Current gauge value",
    aiAliases: ["current", "amount", "level", "reading"],
  },
  min: {
    type: "number",
    default: 0,
    description: "Minimum value of the gauge range",
    aiAliases: ["minimum", "low", "floor"],
  },
  max: {
    type: "number",
    default: 100,
    description: "Maximum value of the gauge range",
    aiAliases: ["maximum", "high", "ceiling", "limit"],
  },
  unit: {
    type: "string",
    default: "%",
    description: "Unit suffix displayed after the value",
    aiAliases: ["suffix", "units", "measure"],
  },
  label: {
    type: "string",
    default: "",
    description: "Label text for the gauge",
    aiAliases: ["title", "name", "heading"],
  },
  colors: {
    type: "array",
    default: ["#3b82f6", "#e5e7eb"],
    description: "Array of colors [fill, track]",
    aiAliases: ["color", "fill color", "gauge color"],
  },
  thresholds: {
    type: "object",
    default: [],
    description: "Threshold breakpoints for color zones",
    aiAliases: ["zones", "ranges", "levels", "breakpoints"],
  },
  needleColor: {
    type: "color",
    default: "#374151",
    description: "Color of the dial needle (dial mode only)",
    aiAliases: ["pointer color", "indicator color"],
  },
  valueColor: {
    type: "color",
    default: "#1f2937",
    description: "Color of the value text",
    aiAliases: ["text color", "number color"],
  },
  roundedSegments: {
    type: "boolean",
    default: false,
    description: "Use rounded ends on arc/bar segments",
    aiAliases: ["rounded", "round corners", "smooth"],
  },
  segmentOpacity: {
    type: "number",
    default: 1,
    description: "Opacity of gauge segments (0-1)",
    aiAliases: ["opacity", "transparency"],
  },
  showRangeLabels: {
    type: "boolean",
    default: false,
    description: "Show min/max labels at arc endpoints (dial mode)",
    aiAliases: ["range labels", "min max labels"],
  },
  ringThickness: {
    type: "number",
    default: 12,
    description: "Thickness of the ring in pixels (ring mode)",
    aiAliases: ["thickness", "ring width", "stroke width"],
  },
  barHeight: {
    type: "number",
    default: 24,
    description: "Height of the progress bar in pixels (bar mode)",
    aiAliases: ["bar size", "bar thickness"],
  },
  barCornerRadius: {
    type: "number",
    default: 12,
    description: "Corner radius of the progress bar (bar mode)",
    aiAliases: ["bar radius", "bar rounding"],
  },
  valueFormat: {
    type: "string",
    default: "{value}",
    description: "Format string for displaying the value",
    aiAliases: ["format", "number format"],
  },
  showValue: {
    type: "boolean",
    default: true,
    description: "Whether to display the numeric value",
    aiAliases: ["show number", "display value"],
  },
  showLabel: {
    type: "boolean",
    default: true,
    description: "Whether to display the label text",
    aiAliases: ["show title", "display label"],
  },
  valueFontSize: {
    type: "number",
    default: 32,
    description: "Font size of the value text in pixels",
    aiAliases: ["value size", "number size"],
  },
  labelFontSize: {
    type: "number",
    default: 14,
    description: "Font size of the label text in pixels",
    aiAliases: ["label size", "title size"],
  },
};

// Chart type configurations
export const chartTypeConfigs: Record<ComponentType, ChartTypeConfig | undefined> = {
  [ComponentType.LINE_CHART]: {
    type: ComponentType.LINE_CHART,
    name: "Line Chart",
    description: "Display trends and changes over time",
    category: "trend",
    properties: {
      ...commonChartProperties,
      ...axisProperties,
      ...lineChartProperties,
    },
    dataStructure: {
      required: ["name", "value"],
      optional: ["value2", "value3"],
      format: "series",
    },
    aiCommands: [
      "add a line chart",
      "show trends over time",
      "plot monthly data",
      "make the line thicker",
      "add dots to the line",
      "use dashed line",
      "smooth the curve",
      "add another line for comparison",
    ],
    relatedTypes: [ComponentType.AREA_CHART, ComponentType.BAR_CHART],
  },
  [ComponentType.BAR_CHART]: {
    type: ComponentType.BAR_CHART,
    name: "Bar Chart",
    description: "Compare values across categories",
    category: "comparison",
    properties: {
      ...commonChartProperties,
      ...axisProperties,
      ...barChartProperties,
    },
    dataStructure: {
      required: ["name", "value"],
      optional: ["value2", "value3"],
      format: "series",
    },
    aiCommands: [
      "add a bar chart",
      "compare categories",
      "show sales by region",
      "stack the bars",
      "make horizontal bars",
      "increase bar spacing",
    ],
    relatedTypes: [ComponentType.LINE_CHART, ComponentType.AREA_CHART],
  },
  [ComponentType.PIE_CHART]: {
    type: ComponentType.PIE_CHART,
    name: "Pie Chart",
    description: "Show proportional data and percentages",
    category: "proportion",
    properties: {
      ...commonChartProperties,
      ...pieChartProperties,
    },
    dataStructure: {
      required: ["name", "value"],
      format: "simple",
    },
    aiCommands: [
      "add a pie chart",
      "show percentages",
      "make it a donut chart",
      "display market share",
      "show labels on slices",
    ],
    relatedTypes: [ComponentType.TREEMAP_CHART],
  },
  [ComponentType.AREA_CHART]: {
    type: ComponentType.AREA_CHART,
    name: "Area Chart",
    description: "Display cumulative totals over time",
    category: "trend",
    properties: {
      ...commonChartProperties,
      ...axisProperties,
      fillOpacity: {
        type: "number",
        default: 0.6,
        description: "Area fill opacity",
        aiAliases: ["transparency", "opacity"],
        validation: (value) => value >= 0 && value <= 1,
      },
      stacked: {
        type: "boolean",
        default: false,
        description: "Stack areas",
        aiAliases: ["cumulative", "stacked areas"],
      },
    },
    dataStructure: {
      required: ["name", "value"],
      optional: ["value2", "value3"],
      format: "series",
    },
    aiCommands: [
      "add an area chart",
      "show cumulative data",
      "fill under the line",
      "stack the areas",
    ],
    relatedTypes: [ComponentType.LINE_CHART],
  },
  [ComponentType.SCATTER_CHART]: {
    type: ComponentType.SCATTER_CHART,
    name: "Scatter Plot",
    description: "Show correlation between two variables",
    category: "distribution",
    properties: {
      ...commonChartProperties,
      ...axisProperties,
      dotSize: {
        type: "number",
        default: 5,
        description: "Size of scatter points",
        aiAliases: ["point size", "marker size"],
      },
    },
    dataStructure: {
      required: ["x", "y"],
      optional: ["z", "category"],
      format: "simple",
    },
    aiCommands: [
      "add a scatter plot",
      "show correlation",
      "plot x vs y",
      "display distribution",
    ],
    relatedTypes: [ComponentType.LINE_CHART],
  },
  // Base types for completeness
  [ComponentType.TEXT]: undefined,
  [ComponentType.CONTAINER]: undefined,
  [ComponentType.FLEX_CONTAINER]: undefined,
  [ComponentType.CARD]: undefined,
  [ComponentType.INPUT]: undefined,
  [ComponentType.BUTTON]: undefined,
  [ComponentType.IMAGE]: undefined,
  [ComponentType.ICON]: undefined,
  [ComponentType.CHART]: undefined,
  [ComponentType.HEATMAP_CHART]: {
    type: ComponentType.HEATMAP_CHART,
    name: "Heatmap Chart",
    description: "Color-coded grid for density visualization, resource allocation, and workload matrices",
    category: "comparison",
    properties: {
      title: { type: "string", default: "Heatmap Chart", description: "Chart title", aiAliases: ["heading", "name", "label"] },
      xAxisKey: { type: "string", default: "x", description: "Field for X-axis categories" },
      yAxisKey: { type: "string", default: "y", description: "Field for Y-axis categories" },
      valueKey: { type: "string", default: "value", description: "Field containing cell values" },
      colors: { type: "array", default: ["#fee5d9","#fcbba1","#fc9272","#fb6a4a","#ef3b2c","#cb181d","#99000d"], description: "Sequential color scale" },
      showCellLabels: { type: "boolean", default: false, description: "Display value text inside cells" },
      cellLabelFormat: { type: "string", default: "{value}", description: "Label format template ({value}h, {value}%)" },
      cellLabelColor: { type: "string", description: "Label text color (auto-contrast if not set)" },
      showRowTotals: { type: "boolean", default: false, description: "Show summary column on right" },
      showColumnTotals: { type: "boolean", default: false, description: "Show summary row at bottom" },
      totalAggregation: { type: "string", default: "sum", description: "Aggregation for totals: sum, average, max, min" },
      rowTotalLabel: { type: "string", default: "Total", description: "Header for totals column" },
      columnTotalLabel: { type: "string", default: "Total", description: "Header for totals row" },
      showLegend: { type: "boolean", default: true, description: "Show color legend" },
      showGrid: { type: "boolean", default: true, description: "Show grid lines" },
      showTooltip: { type: "boolean", default: true, description: "Show tooltip on hover" },
    },
    dataStructure: {
      required: ["x", "y", "value"],
      optional: ["label", "category"],
      format: "matrix",
    },
    aiCommands: [
      "create heatmap", "add heatmap", "heatmap chart",
      "resource matrix", "workload matrix", "team heatmap", "capacity grid",
      "show cell labels", "add row totals", "add column totals",
      "set label format", "change heatmap colors",
    ],
    relatedTypes: [],
  },
  [ComponentType.TREEMAP_CHART]: undefined,
  [ComponentType.GAUGE_CHART]: {
    type: ComponentType.GAUGE_CHART,
    name: "Gauge Chart",
    description: "Display a single metric as a dial, ring, progress bar, or milestone tracker",
    category: "metric",
    properties: {
      ...gaugeChartProperties,
    },
    dataStructure: {
      required: ["value"],
      optional: ["min", "max", "unit", "label"],
      format: "simple",
    },
    aiCommands: [
      "add a gauge",
      "create a dial",
      "show a progress ring",
      "add a progress bar",
      "create a milestone tracker",
      "display a meter",
      "add a speedometer",
    ],
    relatedTypes: [
      ComponentType.VERTICAL_GAUGE_CHART,
      ComponentType.DATA_CARD,
    ],
  },
  [ComponentType.VERTICAL_GAUGE_CHART]: undefined,
  [ComponentType.DATA_CARD]: {
    type: ComponentType.DATA_CARD,
    name: "Data Card",
    description: "Display a single metric value with optional trend indicator and icon",
    category: "metric",
    properties: {
      ...dataCardProperties,
    },
    dataStructure: {
      required: ["value"],
      optional: ["label", "trend", "unit"],
      format: "simple",
    },
    aiCommands: [
      "add a data card",
      "show a metric",
      "display a KPI",
      "create a stat card",
      "add a number display",
      "show total count",
    ],
    relatedTypes: [
      ComponentType.GAUGE_CHART,
      ComponentType.VERTICAL_GAUGE_CHART,
    ],
  },
  [ComponentType.FERMENTATION_TANK_CARD]: undefined,
  [ComponentType.TABLE]: {
    type: ComponentType.TABLE,
    name: "Data Table",
    description: "Display tabular data with sorting, filtering, and pagination",
    category: "comparison",
    properties: {
      title: { type: "string", default: "", description: "Table title", aiAliases: ["heading", "name", "label"] },
      description: { type: "string", default: "", description: "Table description or subtitle", aiAliases: ["subtitle", "caption", "info"] },
      responsive: { type: "boolean", default: true, description: "Responsive sizing", aiAliases: ["auto resize", "flexible", "adaptive"] },
      enableSorting: { type: "boolean", default: true, description: "Enable column sorting" },
      enableFiltering: { type: "boolean", default: true, description: "Enable column filtering" },
      enableGlobalFilter: { type: "boolean", default: true, description: "Enable global search" },
      enablePagination: { type: "boolean", default: true, description: "Enable pagination" },
      pageSize: { type: "number", default: 10, description: "Rows per page" },
      striped: { type: "boolean", default: true, description: "Alternating row colors" },
      hoverable: { type: "boolean", default: true, description: "Highlight rows on hover" },
      maxHeight: { type: "number", default: 400, description: "Maximum table height in pixels" },
    },
    dataStructure: {
      required: [],
      optional: [],
      format: "series",
    },
    aiCommands: [
      "add a data table",
      "show data as table",
      "create a table with sorting",
      "display records in a table",
      "add pagination",
      "enable filtering",
    ],
    relatedTypes: [ComponentType.ADVANCED_TABLE],
  },
  [ComponentType.ADVANCED_TABLE]: {
    type: ComponentType.ADVANCED_TABLE,
    name: "Advanced Table",
    description: "Feature-rich table with virtualization, row selection, column resizing, and hierarchical data",
    category: "comparison",
    properties: {
      title: { type: "string", default: "", description: "Table title", aiAliases: ["heading", "name", "label"] },
      description: { type: "string", default: "", description: "Table description or subtitle", aiAliases: ["subtitle", "caption", "info"] },
      responsive: { type: "boolean", default: true, description: "Responsive sizing", aiAliases: ["auto resize", "flexible", "adaptive"] },
      enableSorting: { type: "boolean", default: true, description: "Enable column sorting" },
      enableFiltering: { type: "boolean", default: true, description: "Enable column filtering" },
      enableGlobalFilter: { type: "boolean", default: true, description: "Enable global search" },
      enableColumnResizing: { type: "boolean", default: true, description: "Enable column resizing" },
      enableRowSelection: { type: "boolean", default: true, description: "Enable row selection" },
      enablePagination: { type: "boolean", default: true, description: "Enable pagination" },
      enableVirtualization: { type: "boolean", default: true, description: "Enable virtual scrolling for large datasets" },
      pageSize: { type: "number", default: 50, description: "Rows per page" },
      maxHeight: { type: "number", default: 600, description: "Maximum table height in pixels" },
      striped: { type: "boolean", default: true, description: "Alternating row colors" },
      hoverable: { type: "boolean", default: true, description: "Highlight rows on hover" },
    },
    dataStructure: {
      required: [],
      optional: [],
      format: "series",
    },
    aiCommands: [
      "add an advanced table",
      "create a table with virtualization",
      "show large dataset as table",
      "add row selection to table",
      "enable column resizing",
    ],
    relatedTypes: [ComponentType.TABLE],
  },
  [ComponentType.LIST]: undefined,
  [ComponentType.CUSTOM]: undefined,
  [ComponentType.MAP_CHART]: undefined,
  [ComponentType.DATA_TEXT]: undefined,
  [ComponentType.FORM_CONTAINER]: undefined,
  [ComponentType.BREADCRUMB]: undefined,
  [ComponentType.FILE_STORAGE]: undefined,
  [ComponentType.KANBAN_BOARD]: undefined,
  [ComponentType.RADIAL_DIAL]: undefined,
  [ComponentType.TOGGLE_SWITCH]: undefined,
  [ComponentType.PUSH_BUTTON]: undefined,
  [ComponentType.ICON_BUTTON]: undefined,
  [ComponentType.STATUS_LIGHT]: undefined,
  [ComponentType.SLIDER]: undefined,
  [ComponentType.STEPPER]: undefined,
  [ComponentType.LEVEL_INDICATOR]: undefined,
  [ComponentType.SEGMENTED_CONTROL]: undefined,
  [ComponentType.COLOR_PICKER]: undefined,
  [ComponentType.THERMOSTAT]: undefined,
  [ComponentType.MODAL]: undefined,
  [ComponentType.CHECKLIST]: undefined,
  [ComponentType.HORIZONTAL_TIMELINE]: undefined,
  [ComponentType.ASSIGNMENT_WIDGET]: undefined,
  [ComponentType.GANTT_CHART]: undefined,
  [ComponentType.CALENDAR]: undefined,
  [ComponentType.PROJECT_HUB]: undefined,
  [ComponentType.QUESTIONNAIRE]: undefined,
  [ComponentType.VOTING_CARD]: undefined,
  [ComponentType.TIME_TRACKER]: undefined,
};

// Registry class for managing chart types
export class ChartTypeRegistry {
  private static instance: ChartTypeRegistry;

  static getInstance(): ChartTypeRegistry {
    if (!ChartTypeRegistry.instance) {
      ChartTypeRegistry.instance = new ChartTypeRegistry();
    }
    return ChartTypeRegistry.instance;
  }

  getConfig(type: ComponentType): ChartTypeConfig | undefined {
    return chartTypeConfigs[type];
  }

  isChartType(type: ComponentType): boolean {
    const chartTypes = [
      ComponentType.CHART,
      ComponentType.BAR_CHART,
      ComponentType.LINE_CHART,
      ComponentType.PIE_CHART,
      ComponentType.SCATTER_CHART,
      ComponentType.AREA_CHART,
      ComponentType.HEATMAP_CHART,
      ComponentType.TREEMAP_CHART,
      ComponentType.GAUGE_CHART,
      ComponentType.VERTICAL_GAUGE_CHART,
    ];
    return chartTypes.includes(type);
  }

  getPropertySchema(type: ComponentType, propertyName: string): ChartPropertySchema | undefined {
    const config = this.getConfig(type);
    return config?.properties[propertyName];
  }

  validateProperty(type: ComponentType, propertyName: string, value: any): boolean {
    const schema = this.getPropertySchema(type, propertyName);
    if (!schema) return false;

    if (schema.validation) {
      return schema.validation(value);
    }

    // Basic type validation
    switch (schema.type) {
      case "number":
        return typeof value === "number" && !isNaN(value);
      case "string":
        return typeof value === "string";
      case "boolean":
        return typeof value === "boolean";
      case "array":
        return Array.isArray(value);
      case "object":
        return typeof value === "object" && value !== null;
      case "enum":
        return schema.enumValues?.includes(value) ?? false;
      case "color":
        return /^#[0-9A-F]{6}$/i.test(value) || /^rgba?\(/.test(value);
      default:
        return true;
    }
  }

  transformProperty(type: ComponentType, propertyName: string, value: any): any {
    const schema = this.getPropertySchema(type, propertyName);
    if (schema?.transform) {
      return schema.transform(value);
    }
    return value;
  }

  getRelatedTypes(type: ComponentType): ComponentType[] {
    const config = this.getConfig(type);
    return config?.relatedTypes || [];
  }

  canConvertTo(fromType: ComponentType, toType: ComponentType): boolean {
    const relatedTypes = this.getRelatedTypes(fromType);
    return relatedTypes.includes(toType);
  }
}

export const chartTypeRegistry = ChartTypeRegistry.getInstance();
